import React, { useState, useEffect, useRef } from "react";
import { Send, Loader, MessageSquare, Search, ChevronLeft } from "lucide-react";
import { Query, ID } from "appwrite";
import { tablesDB, DB_ID, CHATS_TABLE, MESSAGES_TABLE } from "../../appwrite";
import "./AdminChat.css";

const ADMIN_SENDER = "support@trendbayds.com";
const POLL_MS = 2000;

const fmtTime = (v) => {
  if (!v) return "";
  try {
    const d = new Date(v);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return d.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("en-NG", { day: "numeric", month: "short" });
  } catch { return ""; }
};

export default function AdminChat() {
  const [chats, setChats]           = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages]     = useState([]);
  const [text, setText]             = useState("");
  const [sending, setSending]       = useState(false);
  const [search, setSearch]         = useState("");
  const [loadingChats, setLoadingChats] = useState(true);
  const [mobileView, setMobileView] = useState("inbox"); // "inbox" | "convo"
  const bottomRef     = useRef();
  const msgPollRef    = useRef(null);
  const chatPollRef   = useRef(null);
  const pendingIds    = useRef(new Set());
  const selectedIdRef = useRef(selectedId);

  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  // Poll chat list every 3s
  useEffect(() => {
    const fetchChats = async () => {
      try {
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: CHATS_TABLE,
          queries: [Query.orderDesc("lastMessageAt"), Query.limit(200)],
        });
        setChats(res.rows || []);
        setLoadingChats(false);
      } catch { setLoadingChats(false); }
    };

    fetchChats();
    chatPollRef.current = setInterval(fetchChats, 3000);
    return () => { clearInterval(chatPollRef.current); chatPollRef.current = null; };
  }, []);

  // Poll messages for selected chat every 2s
  useEffect(() => {
    if (!selectedId) return;
    setMessages([]);

    const fetchMessages = async () => {
      try {
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: MESSAGES_TABLE,
          queries: [Query.equal("chats", selectedId), Query.orderAsc("$createdAt"), Query.limit(200)],
        });
        const serverMsgs = res.rows || [];
        setMessages((prev) => {
          const serverIds = new Set(serverMsgs.map((m) => m.$id));
          const optimistic = prev.filter((m) => pendingIds.current.has(m.$id) && !serverIds.has(m.$id));
          return [...serverMsgs, ...optimistic];
        });
      } catch (e) { console.error(e); }
    };

    fetchMessages();
    msgPollRef.current = setInterval(fetchMessages, POLL_MS);
    return () => { clearInterval(msgPollRef.current); msgPollRef.current = null; };
  }, [selectedId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendReply = async () => {
    if (!text.trim() || !selectedId || sending) return;
    const body = text.trim();
    const rowId = ID.unique();
    setText("");
    setSending(true);
    pendingIds.current.add(rowId);
    setMessages((prev) => [...prev, {
      $id: rowId, text: body, sender: ADMIN_SENDER, chats: selectedId,
      $createdAt: new Date().toISOString(),
    }]);
    try {
      await tablesDB.createRow({
        databaseId: DB_ID,
        tableId: MESSAGES_TABLE,
        rowId,
        data: { text: body, sender: ADMIN_SENDER, chats: selectedId },
      });
      await tablesDB.updateRow({
        databaseId: DB_ID,
        tableId: CHATS_TABLE,
        rowId: selectedId,
        data: { lastMessageAt: new Date().toISOString() },
      });
      pendingIds.current.delete(rowId);
    } catch (e) {
      console.error(e);
      pendingIds.current.delete(rowId);
      setMessages((prev) => prev.filter((m) => m.$id !== rowId));
    }
    finally { setSending(false); }
  };

  const filteredChats = chats.filter((c) =>
    (c.userEmail || "").toLowerCase().includes(search.toLowerCase())
  );

  const selectedChat = chats.find((c) => c.$id === selectedId);

  return (
    <div className={`ac-page ac-page--${mobileView}`}>

      {/* Left: inbox */}
      <div className="ac-inbox">
        <div className="ac-inbox-header">
          <h2 className="ac-inbox-title">Live Chat</h2>
          <p className="ac-inbox-sub">{chats.length} conversation{chats.length !== 1 ? "s" : ""}</p>
        </div>

        <div className="ac-search-wrap">
          <Search size={14} className="ac-search-icon" />
          <input
            className="ac-search"
            type="text"
            placeholder="Search by email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="ac-chat-list">
          {loadingChats ? (
            <div className="ac-list-empty"><Loader size={20} className="ac-spin" color="#00A855" /></div>
          ) : filteredChats.length === 0 ? (
            <div className="ac-list-empty">No conversations yet</div>
          ) : (
            filteredChats.map((chat) => (
              <button
                key={chat.$id}
                className={`ac-chat-row ${chat.$id === selectedId ? "ac-chat-row--active" : ""}`}
                onClick={() => { setSelectedId(chat.$id); setMobileView("convo"); }}
              >
                <div className="ac-chat-avatar">{(chat.userEmail || "?")[0].toUpperCase()}</div>
                <div className="ac-chat-info">
                  <p className="ac-chat-email">{chat.userEmail || "Unknown"}</p>
                  <p className="ac-chat-time">{fmtTime(chat.lastMessageAt)}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right: conversation */}
      <div className="ac-convo">
        {!selectedId ? (
          <div className="ac-empty-state">
            <div className="ac-empty-icon"><MessageSquare size={32} color="#00A855" /></div>
            <p className="ac-empty-title">Select a conversation</p>
            <p className="ac-empty-sub">Choose a customer from the left to start replying.</p>
          </div>
        ) : (
          <>
            <div className="ac-convo-header">
              <button className="ac-back-btn" onClick={() => setMobileView("inbox")}>
                <ChevronLeft size={20} />
              </button>
              <div className="ac-convo-avatar">{(selectedChat?.userEmail || "?")[0].toUpperCase()}</div>
              <div>
                <p className="ac-convo-email">{selectedChat?.userEmail || "Customer"}</p>
                <p className="ac-convo-label">Customer</p>
              </div>
            </div>

            <div className="ac-messages">
              {messages.map((msg) => {
                const isAdmin = msg.sender === ADMIN_SENDER;
                return (
                  <div key={msg.$id} className={`ac-msg ${isAdmin ? "ac-msg--admin" : "ac-msg--user"}`}>
                    <div className="ac-bubble">{msg.text}</div>
                    <p className="ac-msg-time">{fmtTime(msg.$createdAt)}</p>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            <div className="ac-reply-bar">
              <input
                className="ac-reply-input"
                type="text"
                placeholder="Type a reply…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendReply()}
                disabled={sending}
              />
              <button className="ac-reply-send" onClick={sendReply} disabled={!text.trim() || sending}>
                {sending ? <Loader size={16} className="ac-spin" /> : <Send size={16} />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
